import React from "react";
import { formatContent, formatCreatedAt, formatSubject } from "@/lib/utils/notification-formatter";
import { Banner, Flex, InfoBanner, Typography, useI18nContext } from "@procore/core-react";
import type { MouseEventHandler } from "react";

import { formatName } from "@/lib/utils/preferences";
import type { SerializedNotification } from "@laborchart-modules/lc-core-api/dist/api/notifications/get-user-notifications";
import "./css/home.css";
import { isUnread } from "./notifications-card";

const BANNER_BACKGROUND_COLOR = "hsl(218, 75%, 96%)";
const BANNER_BORDER_LEFT_COLOR = "hsl(218, 75%, 50%)";

export type NotificationBannerProps = {
   notification: SerializedNotification;
   onClick?: MouseEventHandler<HTMLDivElement>;
};

export const NotificationBanner = ({ notification, onClick }: NotificationBannerProps) => {
   const I18n = useI18nContext();
   const content = formatContent(notification, I18n);
   return (
      <InfoBanner
         className="notification-banner"
         style={{
            alignSelf: "stretch",
            flex: "none",
            borderLeftColor: isUnread(notification)
               ? BANNER_BORDER_LEFT_COLOR
               : BANNER_BACKGROUND_COLOR,
            cursor: onClick ? "pointer" : "default",
         }}
         onClick={onClick}
      >
         <Banner.Content style={{ minWidth: 0 }}>
            <Banner.Title>{formatSubject(notification, I18n)}</Banner.Title>
            <Banner.Body>
               <Flex direction="column">
                  {typeof content === "string" ? (
                     <Typography
                        intent="body"
                        color="gray15"
                        style={{
                           overflow: "hidden",
                           textOverflow: "ellipsis",
                           whiteSpace: "nowrap",
                           width: "100%",
                        }}
                     >
                        {content}
                     </Typography>
                  ) : (
                     <>
                        <Typography
                           intent="body"
                           color="gray15"
                           style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                           }}
                        >
                           {content.leadIn}
                        </Typography>
                        {content.listItems.map((item, index) => (
                           <Typography
                              key={index}
                              intent="body"
                              italic
                              color="gray15"
                              style={{
                                 overflow: "hidden",
                                 textOverflow: "ellipsis",
                                 whiteSpace: "nowrap",
                                 width: "100%",
                              }}
                           >
                              {item}
                           </Typography>
                        ))}
                     </>
                  )}
                  <Typography intent="small" color="gray15">
                     {`${formatName({
                        first: notification.author_name.first,
                        last: notification.author_name.last,
                     })} - ${formatCreatedAt(notification.created_at, I18n)}`}
                  </Typography>
               </Flex>
            </Banner.Body>
         </Banner.Content>
      </InfoBanner>
   );
};
